import { Task } from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';

/**
 * Conflict handler for Task entities.
 * Handles version conflicts during task updates with intelligent merging.
 */
export const taskConflictHandler: ConflictHandler<Task> = {
    entityType: 'Task',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<Task> {
        // Note: This is a fallback. The server should include currentState in conflict response.
        // If we need to refetch, we'd need workspaceId and taskListUnitId which we don't have here.
        // For now, throw an error - the server should always provide currentState.
        throw new Error(
            'Task refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<Task>): Partial<Task> {
        // Simple merge strategy: apply intended changes to current state
        // This preserves server-side changes while applying user's intent
        const merged: Partial<Task> = {
            // Always use current version
            version: currentState.version,
            // Preserve ID
            id: currentState.id
        };

        // Apply intended changes
        if (intendedChanges.name !== undefined) {
            merged.name = intendedChanges.name;
        }
        if (intendedChanges.description !== undefined) {
            merged.description = intendedChanges.description;
        }
        if (intendedChanges.isArchived !== undefined) {
            merged.isArchived = intendedChanges.isArchived;
        }
        if (intendedChanges.scheduleRule !== undefined) {
            merged.scheduleRule = intendedChanges.scheduleRule;
        }
        if (intendedChanges.relatedPage !== undefined) {
            merged.relatedPage = intendedChanges.relatedPage;
        }
        if (intendedChanges.config !== undefined) {
            merged.config = intendedChanges.config;
        }
        if (intendedChanges.images !== undefined) {
            merged.images = intendedChanges.images;
        }
        if (intendedChanges.taskListUnitId !== undefined) {
            merged.taskListUnitId = intendedChanges.taskListUnitId;
        }
        if (intendedChanges.taskListId !== undefined) {
            merged.taskListId = intendedChanges.taskListId;
        }

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
